// LoginCountWebSocketInitializer.tsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { FeedActionContext } from './FeedState';
import { useStateSelector } from '../global/dataSelectors';
import { useDispatcher } from '../global/hooks';
import { createSelector } from 'reselect';
import { DataState } from '../global/state';
import { useLoginCountFeedStatus, useLoginCountWebSocketCloseReason } from './FeedSelectors';
import { Websockets } from '../../constants';
import _ from 'lodash';
import { loginCountWebSocketService } from './LoginCountWebSocketService';
import { account } from '../../data/global/selectors/accountSelectors';
import { useSelector } from 'react-redux';
import { UserProfileLevel } from '@tradingblock/types';
import { useFeatureFlag } from '@tradingblock/components';

const canConnectToWebsocket = createSelector(
  (s: DataState) => s.auth,
  auth => (auth.isExpired !== true && auth.isAuthenticated === true ? true : false)
);

const LoginCountWebSocketInitializer: React.FC = () => {
  const dispatch = useContext(FeedActionContext);
  const [shouldReconnect, setShouldReconnect] = useState(false);
  const retryTimeoutMs = useRef<number>(250);
  const retryTimeout = useRef<NodeJS.Timeout>();
  const dispatchers = useDispatcher();
  const dispatcher = dispatchers.dispatcher.loginCountFeed;
  const globalDispatch = dispatchers.dispatch;
  const loginCountFeedStatus = useLoginCountFeedStatus();
  const closeReason = useLoginCountWebSocketCloseReason();
  const canConnect = useStateSelector(canConnectToWebsocket);
  const apiToken = useStateSelector(state => state.auth.apiToken);
  const userLevel = useSelector(account.userLevel);
  const { isEnabled: useLofiLoginCount } = useFeatureFlag('lofi-login-count');

  useEffect(() => {
    return () => {
      if (userLevel !== UserProfileLevel.Account && useLofiLoginCount) {
        loginCountWebSocketService.sendMessage(JSON.stringify({ type: 'Unsubscribe' }));
      }
    };
  }, []);

  useEffect(() => {
    if (loginCountFeedStatus && loginCountFeedStatus === 'open') {
      if (userLevel !== UserProfileLevel.Account && useLofiLoginCount) {
        loginCountWebSocketService.sendMessage(JSON.stringify({ type: 'Subscribe' }));
      }
    }
  }, [loginCountFeedStatus, useLofiLoginCount]);

  useEffect(() => {
    if (shouldReconnect && apiToken) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        if (loginCountWebSocketService.shouldTryReconnect()) {
          loginCountWebSocketService
            .connect(dispatch, dispatcher, globalDispatch, apiToken)
            .then(() => setShouldReconnect(false));
        }
      }, retryTimeoutMs.current);
    }
  }, [shouldReconnect, apiToken]);

  useEffect(() => {
    if (loginCountFeedStatus === 'closed') {
      if (canConnect) {
        const newTimeout = Math.min(Websockets.ReconnectInterval, retryTimeoutMs.current + retryTimeoutMs.current);
        const newTimeoutDisplay = _.round(newTimeout / 1000, 2);
        const closeMessage = `Login Count Socket is closed. Reconnect will be attempted in ${newTimeoutDisplay} second.`;
        dispatcher.closed({
          reason: closeReason || '',
          retryingInSeconds: newTimeoutDisplay,
          message: closeMessage,
        });
        retryTimeoutMs.current = newTimeout;
        console.warn(closeMessage);
        setShouldReconnect(true);
      } else {
        const closeMessage = 'Login Count Socket is closed because user can no longer connect';
        dispatcher.closed({ reason: closeReason || '', message: closeMessage });
        console.warn(closeMessage);
      }
    }
  }, [loginCountFeedStatus, closeReason, canConnect]);

  useEffect(() => {
    if (canConnect && apiToken) {
      console.log('initializing login count websocket (this should only happen once!)');
      loginCountWebSocketService.connect(dispatch, dispatcher, globalDispatch, apiToken);
    } else if (!canConnect && !loginCountWebSocketService.shouldTryReconnect()) {
      console.log('disconnecting login count websocket because user can no longer connect');
      loginCountWebSocketService.closeConnection();
    } else {
      console.log('doing nothing');
    }
  }, [dispatcher, canConnect, apiToken]);

  return null; // This component does not render anything
};

export default LoginCountWebSocketInitializer;
